import type { ReactNode } from 'react';
import { useMemo, useRef, useLayoutEffect, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { AppLink } from '@/shared/components/AppLink';
import { ExploreOtherDesignsWidget } from '@/shared/widgets/ExploreOtherDesignsWidget';
import type { UsePlpListingReturn } from '../hooks/usePlpListing';
import { usePlpColumns } from '../hooks/usePlpColumns';
import { PlpGrid } from '../components/PlpGrid';
import { PlpSkeleton } from '../components/PlpSkeleton';
import { Skeleton } from '@/shared/components/Skeleton';
import { PlpNoResults } from '../components/PlpNoResults';
import { PlpNoStoreFound } from '../components/PlpNoStoreFound';
import { PlpSeoHead } from '@/shared/features/seo/presentation/components/PlpSeoHead';
import { CertificationSection, PlpSeoTextBlock } from '@/shared/features/seo/presentation/components/PlpSeoTextBlock';
import { buildPlpCells } from '../components/plpCells.util';
import { useStickyOnScroll } from '@/core/hooks/useStickyOnScroll';
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { useRevealOnScrollUpWhile } from '@/core/hooks/useRevealOnScrollUpWhile';
import { useScrollDirection } from '@/core/hooks/useScrollDirection';
import { GoToTopFab } from '@/shared/widgets/GoToTopFab';
import { PlpHeader } from '@/features/plp/presentation/components/PlpHeader';
import { PlpBreadcrumb } from '../components/PlpBreadcrumb';
import { PlpTopFilterBar } from '@/features/filters/presentation/components/PlpTopFilterBar';
import { PlpBottomBar } from '../components/PlpBottomBar';
import { SelectedStoreWidget, type StoreWidgetData } from './SelectedStoreWidget';
import { BrowseBannerSlot } from '@/shared/features/banner-widget/presentation/components/BrowseBannerSlot';
import type { PlpSortByValue } from '../../constants';
import { PLP_BROWSE_PAGE_MAX_CLASS } from '../../constants';
import type { PlpBreadcrumbItem } from '../../utils/plpBreadcrumb';

export interface PlpBaseProps extends UsePlpListingReturn {
  renderExploreLinks?: () => ReactNode;
  renderBottomBar?: () => ReactNode;
  countLabel?: string;
  pincode?: string;
  onOpenPincode?: () => void;
  onOpenSort?: () => void;
  onOpenFilter?: () => void;
  onActivateStoreDesigns?: () => void;
  onShowAllDesigns?: () => void;
  isStoreDesignsActive?: boolean;
  isStorePlp?: boolean;
  /** When true, show store city/store selector even if PLP fetch is deferred. */
  isStoreCitySelectionMode?: boolean;
  selectedStoreData?: StoreWidgetData | null;
  selectedStoresData?: StoreWidgetData[];
  isStoreWidgetLoading?: boolean;
  storeCitySlug?: string;
  cityToStoresMap?: unknown;
  /** Current listing sort (URL); used for desktop top-bar sort label. */
  currentSortBy?: PlpSortByValue;
  /** Desktop: apply sort from inline dropdown (mobile still uses `onOpenSort` + sheet). */
  onApplySort?: (sort: PlpSortByValue) => void;
  /** Desktop: sort dropdown opened by hovering the sort button (analytics). */
  onSortHover?: () => void;
  /** Jewellery browse PLP: breadcrumb trail (desktop + mobile). */
  breadcrumbItems?: PlpBreadcrumbItem[];
  /** When true, PDP links from this listing open in a new browser tab. */
  openPdpInNewTab?: boolean;
  /**
   * Desktop: renders the always-visible left filter sidebar.
   * Hidden on mobile — mobile uses the bottom-sheet (`PlpFilterSheet`).
   */
  renderDesktopFilterSidebar?: () => ReactNode;
  /** Slot rendered below the product grid (e.g. nearby stores, store FAQ). */
  renderBelowGrid?: () => ReactNode;
  /**
   * Desktop only: when true (filter selected, awaiting 2 s nav timer), show the
   * product-card skeleton immediately instead of waiting for the page transition.
   */
  isDesktopPendingNavigation?: boolean;
  /** When true, inserts the physical store location banner widget into the product grid (non-store PLPs only). */
  hasStoreLocationWidget?: boolean;
  /** When true, renders the "no stores found" empty state in place of the product grid. */
  isNoStoreFound?: boolean;
  /** Called when user taps "Change Pincode" in the no-store-found state. */
  onChangePincodeForStore?: () => void;
  /** Called when user taps "Explore Other Designs" in the no-store-found state. */
  onExploreAllDesigns?: () => void;
  /** Analytics: called when user clicks a product card */
  onProductClick?: (product: import('../../domain/models/ProductListing').ListingDesignItem, scrollDepth?: number, horizontalPosition?: number) => void;
  /** Analytics: eventLogger on wishlist icon tap (before API) */
  onWishlistClick?: (
    product: import('../../domain/models/ProductListing').ListingDesignItem,
    productPosition: number,
  ) => void;
  /** Analytics: called when user toggles the wishlist icon */
  onWishlistToggle?: (
    product: import('../../domain/models/ProductListing').ListingDesignItem,
    action: 'add' | 'remove',
  ) => void;
  /** Analytics: called when user clicks the video play button */
  onVideoClick?: (
    product: import('../../domain/models/ProductListing').ListingDesignItem,
    productPosition: number,
  ) => void;
  onVideoCallCartAdd?: (product: import('../../domain/models/ProductListing').ListingDesignItem) => void;
  onViewDetailsClick?: (product: import('../../domain/models/ProductListing').ListingDesignItem) => void;
  /** When true, hides the Book Video Call button on all product cards. */
  hideVideoCallButton?: boolean;
  /** Analytics: eventLogger product impression after 2s full visibility */
  onProductViewed?: (params: {
    product: import('../../domain/models/ProductListing').ListingDesignItem;
    scrollDepth: number;
    horizontalPosition: number;
  }) => void;
  viewedProductIds?: Set<number>;
  /** When false, skip in-grid banner cells (image search PLP). Default true. */
  hasBanners?: boolean;
  /** When true, hide filter bar, filter chips, desktop sidebar slot, and mobile bottom bar. */
  hideFilterBar?: boolean;
}

export function PlpBase({
  listingMeta,
  allDesigns,
  hasMore,
  isLoading,
  currentPage: _currentPage,
  isLoadingMore,
  isError,
  loadMore,
  onBeforeNavigate,
  sentinelRef,
  renderExploreLinks,
  renderBottomBar,
  countLabel = 'Designs',
  pincode,
  onOpenPincode,
  onOpenSort,
  onOpenFilter,
  onActivateStoreDesigns,
  onShowAllDesigns,
  redirectUrl,
  isStoreDesignsActive = false,
  isStorePlp = false,
  isStoreCitySelectionMode = false,
  selectedStoreData,
  selectedStoresData,
  isStoreWidgetLoading = false,
  storeCitySlug,
  cityToStoresMap,
  currentSortBy,
  onApplySort,
  onSortHover,
  breadcrumbItems,
  openPdpInNewTab = false,
  renderDesktopFilterSidebar,
  renderBelowGrid,
  isDesktopPendingNavigation = false,
  hasStoreLocationWidget = false,
  isNoStoreFound = false,
  onChangePincodeForStore,
  onExploreAllDesigns,
  onProductClick,
  onWishlistClick,
  onWishlistToggle,
  onVideoClick,
  onVideoCallCartAdd,
  onViewDetailsClick,
  onProductViewed,
  viewedProductIds,
  suppressShimmerScrollToTop = false,
  mobileRestoreDesignId = null,
  mobileScrollRestoreOffset = null,
  onMobileScrollRestoreComplete,
  shouldPauseInfiniteScroll,
  hasBanners = true,
  hideFilterBar = false,
  hideVideoCallButton = false,
}: PlpBaseProps) {
  const columnCount = usePlpColumns();
  const isMobile = useIsMobile();
  const { sentinelRef: stickySentinelRef, isSticky } = useStickyOnScroll();
  const scrollDirection = useScrollDirection();
  const goToTopFabEnabled = isMobile && !suppressShimmerScrollToTop;
  const goToTopOpen = useRevealOnScrollUpWhile(goToTopFabEnabled, { minScrollY: 96 });

  // Measure sticky filter bar height so sidebar can sit just below it.
  const stickyFilterBarRef = useRef<HTMLDivElement>(null);
  const [filterBarHeight, setFilterBarHeight] = useState(0);
  useLayoutEffect(() => {
    const el = stickyFilterBarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setFilterBarHeight(el.offsetHeight));
    ro.observe(el);
    setFilterBarHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  // Measure sticky desktop navbar height so sidebar top collapses to just below it
  // when the top filter bar is hidden (scroll-up state).
  // Uses ResizeObserver subscription so setState is called from a callback (not
  // synchronously in the effect body), satisfying react-hooks/set-state-in-effect.
  const [navbarHeight, setNavbarHeight] = useState(50);
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const nav = document.querySelector<HTMLElement>('.hdr-desktop nav.group');
    if (!nav) return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.borderBoxSize?.[0]?.blockSize ?? (entries[0]?.target as HTMLElement).offsetHeight;
      setNavbarHeight(h);
    });
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);

  const isFilterVisible = !isSticky || scrollDirection === 'down';
  // When filter bar is visible → sit below it; when hidden (scroll-up) → sit just below sticky navbar.
  const sidebarTop = isFilterVisible ? filterBarHeight + 8 : navbarHeight + 8;

  // PDP back: browser/history can leave the window scrolled to the footer. Snap to top first (no animation)
  // before shimmer or grid paint, then PlpGrid applies the saved Y on the next frame(s).
  useLayoutEffect(() => {
    if (!suppressShimmerScrollToTop || typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [suppressShimmerScrollToTop]);

  // True while the grid area is showing the reload shimmer:
  //  - `isLoading`: fetching new listing (URL identity differs from resolved data)
  //  - `isDesktopPendingNavigation`: desktop sidebar 2 s debounce window before navigate
  // Used both for the auto-scroll-to-top below and to hide the grid / explore /
  // SEO sections while the shimmer is on screen.
  const gridReloadShimmer = isLoading || isDesktopPendingNavigation;

  // Scroll to top the moment the loading shimmer appears (desktop filter navigation).
  // Skipped on the initial server-rendered load (isLoading starts false when initialData exists).
  const prevShimmerRef = useRef(false);
  useEffect(() => {
    if (suppressShimmerScrollToTop) return;
    if (gridReloadShimmer && !prevShimmerRef.current && typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    prevShimmerRef.current = gridReloadShimmer;
  }, [gridReloadShimmer, suppressShimmerScrollToTop]);

  const selectedFilterChips = useMemo(() => {
    const filters = listingMeta?.filters ?? [];
    return filters.flatMap((f) =>
      f.items.filter((item) => item.selected).map((item) => ({ f, item }))
    );
  }, [listingMeta?.filters]);

  const exploreLinks = listingMeta?.listingSeo?.seoLinks ?? listingMeta?.exploreLinks ?? [];
  const hasExploreLinks = exploreLinks.length > 0;

  const cells = useMemo(
    () => buildPlpCells(allDesigns, columnCount, hasExploreLinks, hasStoreLocationWidget, hasBanners),
    [allDesigns, columnCount, hasExploreLinks, hasStoreLocationWidget, hasBanners]
  );
  const defaultExploreSection =
    hasExploreLinks ? (
      <ExploreOtherDesignsWidget links={exploreLinks} />
    ) : null;

  const pageTitle = listingMeta?.listingSeo?.header ?? listingMeta?.header ?? '';
  const listingSeo = listingMeta?.listingSeo;

  const isNoResults = !isLoading && !isError && allDesigns.length === 0;
  const shouldShowStoreHeader = isStorePlp && isStoreCitySelectionMode;
  const showTopSection = !isNoResults || shouldShowStoreHeader || isNoStoreFound;
  const showNoResults = isNoResults && !shouldShowStoreHeader && !isNoStoreFound;

  const showCountInDesktopHeader =
    Boolean(breadcrumbItems?.length) && listingMeta?.totalCount != null;

  const showCertificationSection =
    !isStorePlp && !gridReloadShimmer && (!isNoResults || hideFilterBar);

  /** Image search empty/error: pin certification above the site footer. */
  const pinCertificationToBottom =
    hideFilterBar && !isLoading && !gridReloadShimmer && (isNoResults || isError);

  /** Image search PLP: inset header + grid from viewport edges on desktop. */
  const browsePageShellClass = hideFilterBar
    ? `${PLP_BROWSE_PAGE_MAX_CLASS} md:px-4`
    : PLP_BROWSE_PAGE_MAX_CLASS;

  const browseShell = (children: ReactNode) => (
    <div className={browsePageShellClass}>{children}</div>
  );

  // Use suppressShimmerScrollToTop (restore in hook) so the overlay shows on the first paint.
  // Hook mobile props depend on useIsMobile(), which is false until after mount — too late for the loader.
  // md:hidden limits this to mobile viewport without relying on JS media state timing.
  const showMobileRestoreOverlay = suppressShimmerScrollToTop;

  return (
    <div
      className={[
        'flex flex-col overflow-visible',
        pinCertificationToBottom ? 'flex-1' : 'min-h-[calc(100vh-56px)]',
      ].join(' ')}
    >
      {showMobileRestoreOverlay && (
        <div
          className="fixed inset-0 z-[100] flex cursor-wait items-center justify-center bg-white md:hidden"
          aria-busy="true"
          aria-live="polite"
        >
          <Loader2 className="h-10 w-10 animate-spin text-[#4A7B9D]" aria-hidden />
        </div>
      )}
      <PlpSeoHead seo={listingSeo} redirectUrl={redirectUrl} />

      {showTopSection && (
        <>
          {isStorePlp ? (
            <>
              {breadcrumbItems?.length ? (
                <div className="hidden md:block">
                  <div className={`${PLP_BROWSE_PAGE_MAX_CLASS} px-8 py-3`}>
                    <PlpBreadcrumb items={breadcrumbItems} />
                  </div>
                </div>
              ) : null}
              <SelectedStoreWidget
                selectedStore={selectedStoreData}
                selectedStores={selectedStoresData}
                isLoading={isStoreWidgetLoading}
                storeCitySlug={storeCitySlug}
                cityToStoresMap={cityToStoresMap}
                totalCount={listingMeta?.totalCount}
              />
            </>
          ) : (
            browseShell(
              <>
                <PlpHeader
                  title={pageTitle}
                  imageUrl={listingMeta?.headerImageUrl}
                  totalCount={listingMeta?.totalCount}
                  countLabel={countLabel}
                  breadcrumbItems={breadcrumbItems}
                  renderDesktopBanner={<BrowseBannerSlot />}
                  insetFromParent={hideFilterBar}
                />
              </>
            )
          )}

          {!hideFilterBar && (
          <>
              <div ref={stickySentinelRef} className="md:h-4" aria-hidden />

              <div
                ref={stickyFilterBarRef}
                className={`sticky top-0 z-20 mx-auto w-full flex-shrink-0 mb-4 ${isFilterVisible ? '' : 'pointer-events-none'}`}
                style={{
                  maxWidth: isSticky ? '100%' : `1490px`,
                  transition: 'max-width 0.1s ease-out',
                }}
              >
                <div
                  className={[
                    'transform-gpu transition-transform duration-200 ease-out',
                    isFilterVisible ? 'translate-y-0' : '-translate-y-full',
                  ].join(' ')}
                >
                  {/* Full-width background; content constrained inside browseShell */}
                  {/* On mobile, store PLP has no bar content (buttons hidden, sort/pincode are desktop-only) */}
                  <div
                    className={isStorePlp ? 'hidden md:block' : undefined}
                    style={{ backgroundColor: isStorePlp ? 'var(--store-plp-top-bar-bg)' : 'var(--plp-top-bar-bg)' }}
                  >
                    {browseShell(
                      <PlpTopFilterBar
                        onActivateStoreDesigns={isStorePlp ? undefined : onActivateStoreDesigns}
                        onShowAllDesigns={isStorePlp ? undefined : onShowAllDesigns}
                        isStoreDesignsActive={isStoreDesignsActive}
                        pincode={pincode}
                        onOpenPincode={onOpenPincode}
                        onOpenSort={onOpenSort}
                        onApplySort={onApplySort}
                        onSortHover={onSortHover}
                        hidePincode={isStorePlp}
                        currentSortBy={currentSortBy}
                      />
                    )}
                  </div>
                  <section className="border-b border-gray-200 bg-white">
                    {browseShell(
                      gridReloadShimmer ? (
                        /* Skeleton that matches the height of the real count + chips bar */
                        <div className="flex w-full items-center gap-2 px-4 py-3">
                          <Skeleton className="h-[34px] w-24 rounded-md" />
                          <Skeleton className="h-[34px] w-28 rounded-md" />
                        </div>
                      ) : (
                        <div className={[
                            'flex w-full gap-2 px-4 py-3 text-sm text-gray-900',
                            // Store PLP mobile: stack title above chips; desktop keeps row layout.
                            // items-stretch so the chip row is viewport-wide and can scroll horizontally.
                            isStorePlp && showCountInDesktopHeader
                              ? 'flex-col items-stretch md:flex-row md:items-center'
                              : 'items-center',
                          ].join(' ')}>
                          {!isNoStoreFound && (
                            <span
                              className={[ 
                                'min-w-[70px]',
                                showCountInDesktopHeader ? 'md:hidden' : '',
                                // On mobile for store PLP: pink colour, normal weight — matches design
                                isStorePlp && showCountInDesktopHeader
                                  ? 'text-[#c4a7b8] font-normal'
                                  : 'font-bold',
                              ].join(' ')}
                            >
                              {listingMeta?.totalCount != null
                                ? `${isStorePlp && showCountInDesktopHeader ? 'Browse ' : ''}${listingMeta.totalCount} ${countLabel}`
                                : `${isStorePlp && showCountInDesktopHeader ? 'Browse ' : ''}${countLabel}`}
                            </span>
                          )}
                          {!isNoStoreFound && selectedFilterChips.length > 0 && (
                            <span
                              className="hidden shrink-0 md:block text-xs font-semibold uppercase tracking-widest"
                              style={{ color: isStorePlp ? 'var(--store-plp-top-bar-bg)' : undefined }}
                            >
                              Filtered by
                            </span>
                          )}
                          {!isNoStoreFound && selectedFilterChips.length > 0 && (
                            <div
                              className={[
                                'flex min-w-0 flex-1 gap-2 overflow-x-auto',
                                isStorePlp && showCountInDesktopHeader
                                  ? 'w-full max-w-full md:w-auto'
                                  : '',
                                '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
                              ].join(' ')}
                            >
                              {selectedFilterChips.map(({ f, item }) =>
                                item.url ? (
                                  <AppLink
                                    key={`${f.category}-${item.name}`}
                                    to={item.url}
                                    shouldRefresh={false}
                                    className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white py-1.5 pl-3 pr-1.5 text-[13px] hover:bg-gray-50"
                                    style={{ border: `1px solid ${isStorePlp ? 'var(--store-plp-top-bar-bg)' : 'rgb(209,213,219)'}` }}
                                    aria-label={`Remove ${item.displayName}`}
                                  >
                                    <span className="whitespace-nowrap">{item.displayName}</span>
                                    <span className="ml-0.5 font-medium text-red-500">×</span>
                                  </AppLink>
                                ) : (
                                  <span
                                    key={`${f.category}-${item.name}`}
                                    className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white py-1.5 pl-3 pr-1.5 text-[13px]"
                                    style={{ border: `1px solid ${isStorePlp ? 'var(--store-plp-top-bar-bg)' : 'rgb(209,213,219)'}` }}
                                  >
                                    <span className="whitespace-nowrap">{item.displayName}</span>
                                    <span className="ml-0.5 font-medium text-red-500">×</span>
                                  </span>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </section>
                </div>
              </div>
            </>
          )}
        </>
      )}

      <div
        className={[
          browsePageShellClass,
          pinCertificationToBottom ? 'flex flex-1 flex-col' : '',
        ].filter(Boolean).join(' ')}
      >
        {/* Explore other designs — show shimmer while loading to prevent layout shift */}
        {!isNoResults && (
          gridReloadShimmer ? (
            // Reserve the same height as the real explore bar so the grid doesn't jump.
            <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-gray-100">
              <Skeleton className="h-4 w-32 shrink-0 rounded" />
              <div className="flex gap-4 overflow-hidden">
                <Skeleton className="h-4 w-16 shrink-0 rounded" />
                <Skeleton className="h-4 w-20 shrink-0 rounded" />
                <Skeleton className="h-4 w-14 shrink-0 rounded" />
              </div>
            </div>
          ) : (renderExploreLinks || defaultExploreSection) ? (
            // On desktop the explore-links are embedded inside the grid (between rows 8–9).
            // Show this above-grid section on mobile only.
            <div className={!renderExploreLinks ? 'md:hidden' : undefined}>
              {renderExploreLinks ? renderExploreLinks() : defaultExploreSection}
            </div>
          ) : null
        )}

        {/* Desktop: 2-column layout (sidebar + grid). Mobile: single column. */}
        <div
          className={[
            !isNoStoreFound && !hideFilterBar && renderDesktopFilterSidebar
              ? 'md:flex md:items-start md:gap-4 md:px-4'
              : undefined,
            pinCertificationToBottom ? 'flex flex-1 flex-col' : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {!isNoStoreFound && !hideFilterBar && renderDesktopFilterSidebar && (
            <div
              className="hidden self-start md:flex md:flex-col"
              style={{
                position: 'sticky',
                top: sidebarTop,
                transition: 'top 200ms ease-out',
                height: `calc(100vh - ${sidebarTop}px - 16px)`,
                background: '#f7f7f7',
              }}
            >
              {renderDesktopFilterSidebar()}
            </div>
          )}

          <section
            className={[
              'relative flex-1 min-w-0',
              pinCertificationToBottom ? 'flex flex-1 flex-col' : '',
            ].filter(Boolean).join(' ')}
          >
            <div
              className={[
                'w-full px-3 pt-3 md:px-0 md:pt-0',
                pinCertificationToBottom ? 'flex flex-1 flex-col pb-3' : 'pb-20 md:pb-3',
              ].filter(Boolean).join(' ')}
            >
              {isError && (
                <p
                  className={[
                    'text-center text-xs text-red-500',
                    pinCertificationToBottom
                      ? 'flex flex-1 items-center justify-center'
                      : 'mb-3',
                  ].join(' ')}
                >
                  Failed to load designs. Please try again.
                </p>
              )}

              {gridReloadShimmer && (
                <PlpSkeleton showFilterPills={false} />
              )}

              {!isLoading && !isError && showNoResults && (
                <PlpNoResults compact={hideFilterBar} />
              )}

              {isNoStoreFound && (
                <PlpNoStoreFound
                  onChangePincode={onChangePincodeForStore ?? (() => {})}
                  onExploreAll={onExploreAllDesigns ?? (() => {})}
                />
              )}

              {!isNoStoreFound && !gridReloadShimmer && allDesigns.length > 0 && (
                <PlpGrid
                  cells={cells}
                  columnCount={columnCount}
                  onBeforeNavigate={onBeforeNavigate}
                  sentinelRef={sentinelRef}
                  onLoadMore={loadMore}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  openPdpInNewTab={openPdpInNewTab}
                  exploreLinks={exploreLinks}
                  pincode={pincode}
                  onProductClick={onProductClick}
                  onWishlistClick={onWishlistClick}
                  onWishlistToggle={onWishlistToggle}
                  onVideoClick={onVideoClick}
                  onVideoCallCartAdd={onVideoCallCartAdd}
                  onViewDetailsClick={onViewDetailsClick}
                  onProductViewed={onProductViewed}
                  viewedProductIds={viewedProductIds}
                  mobileRestoreDesignId={mobileRestoreDesignId}
                  mobileScrollRestoreOffset={mobileScrollRestoreOffset}
                  onMobileScrollRestoreComplete={onMobileScrollRestoreComplete}
                  shouldPauseInfiniteScroll={shouldPauseInfiniteScroll}
                  hideVideoCallButton={hideVideoCallButton}
                />
              )}

              {!isNoStoreFound && hasMore && isLoadingMore && (
                <div className="mx-auto my-4 flex h-[120px] max-w-[750px] items-center justify-center rounded-lg border border-[#4A7B9D]">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#4A7B9D]" />
                </div>
              )}

            </div>

            {!isNoResults && !hideFilterBar && (renderBottomBar ? (
              renderBottomBar()
            ) : (
              <PlpBottomBar
                pincode={pincode}
                onOpenPincode={onOpenPincode}
                onOpenSort={isNoStoreFound ? undefined : onOpenSort}
                onOpenFilter={isNoStoreFound ? undefined : onOpenFilter}
                hidePincode={isStorePlp}
                variant={isStorePlp ? 'store' : 'default'}
              />
            ))}
          </section>
        </div>

        {!isNoResults && !gridReloadShimmer && renderBelowGrid?.()}

        {showCertificationSection && (
          <div className={pinCertificationToBottom ? 'mt-auto' : 'mt-8 mx-4'}>
            {!hideFilterBar && !isNoResults && (
              <PlpSeoTextBlock seoText={listingSeo?.seoText} title={pageTitle} faqs={listingMeta?.faqs} />
            )}
            <CertificationSection />
          </div>
        )}
      </div>

      <GoToTopFab
        open={goToTopOpen}
        offsetBottomClassName="bottom-[var(--go-to-top-mobile-bottom)]"
        zIndexClassName="z-[60]"
      />
    </div>
  );
}

